import { lerpColor } from '../../color';
import { hsl, type World as Look } from '../../world';
import { calmAt, calmCenter, glowDot, grain, hatchCircle, rgba, sketchCircle, sketchLine, starPath, vignette, type Painter } from '../../art/paint';

const TAU = Math.PI * 2;
const FAR = hsl(330, 34, 16);
const HEIGHTS = [1, 0.95, 1.03, 0.97, 1.02, 0.93, 1.04, 0.96, 1, 0.94, 1.03, 0.98];
const FLIP_ANGLE = 0.45;

interface Tones {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly rim: number;
  readonly L: number;
  readonly A1: number;
  readonly A2: number;
}

interface Point {
  readonly x: number;
  readonly y: number;
}

interface Trio {
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

function tonesOf(look: Look): Tones {
  const pal = look.palette;
  return { bg: pal.bg, far: FAR, ceil: look.ceil, ink: pal.ink, rim: pal.rim, L: pal.incoming.circle, A1: pal.held, A2: pal.spot };
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const k = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + k, y);
  ctx.lineTo(x + w - k, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + k);
  ctx.lineTo(x + w, y + h - k);
  ctx.quadraticCurveTo(x + w, y + h, x + w - k, y + h);
  ctx.lineTo(x + k, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - k);
  ctx.lineTo(x, y + k);
  ctx.quadraticCurveTo(x, y, x + k, y);
  ctx.closePath();
}

function room(p: Painter, t: Tones, floor: number): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.5)));
  g.addColorStop(Math.min(0.9, floor / p.h), rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.far, 0.7)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.1), 0.6);
  ctx.lineWidth = 2;
  sketchLine(ctx, -10, floor, p.w + 10, floor + 2, p.rng, 0.003);
}

function carpet(p: Painter, t: Tones, floor: number): void {
  const ctx = p.ctx;
  const s = Math.max(3, p.unit * 0.014);
  ctx.lineWidth = 1.3;
  ctx.lineCap = 'round';
  for (let i = 0; i < 44; i++) {
    const x = p.rng.float() * p.w;
    const y = floor + 12 + p.rng.float() * Math.max(1, p.h - floor - 12);
    const kind = p.rng.int(0, 3);
    const tone = lerpColor(t.far, p.rng.pick([t.L, t.A1, t.A2]), 0.3);
    const turn = p.rng.float() * TAU;
    if (calmAt(p, x, y, 1.02)) continue;
    ctx.strokeStyle = rgba(tone, 0.45);
    if (kind === 0) {
      starPath(ctx, x, y, s, 0.45, 5, turn);
      ctx.stroke();
    } else if (kind === 1) {
      ctx.beginPath();
      ctx.arc(x, y, s * 0.8, 0, TAU);
      ctx.stroke();
    } else {
      const ux = Math.cos(turn) * s * 1.5;
      const uy = Math.sin(turn) * s * 1.5;
      ctx.beginPath();
      ctx.moveTo(x - ux, y - uy);
      ctx.bezierCurveTo(x - ux / 3 - uy * 0.6, y - uy / 3 + ux * 0.6, x + ux / 3 + uy * 0.6, y + uy / 3 - ux * 0.6, x + ux, y + uy);
      ctx.stroke();
    }
  }
}

function screenStatic(p: Painter, t: Tones, x: number, y: number, w: number, h: number): void {
  const ctx = p.ctx;
  for (let i = 0; i < 18; i++) {
    const len = p.rng.range(1, Math.max(1.5, w * 0.12));
    const px = x + 2 + p.rng.float() * Math.max(1, w - 4 - len);
    const py = y + 2 + p.rng.float() * Math.max(1, h - 4);
    ctx.fillStyle = rgba(lerpColor(t.far, t.A1, 0.45), p.rng.range(0.12, 0.36));
    ctx.fillRect(px, py, len, 1.3);
  }
}

function cabinet(p: Painter, t: Tones, x: number, base: number, cw: number, ch: number, detail: boolean, lit: boolean, tilt: boolean): void {
  const ctx = p.ctx;
  const top = base - ch;
  const left = x - cw / 2;
  ctx.fillStyle = rgba(t.far);
  ctx.fillRect(left, top + ch * 0.12, cw, ch * 0.88 + 2);
  ctx.strokeStyle = rgba(t.ink, 0.6);
  ctx.lineWidth = 1.5;
  ctx.strokeRect(left, top + ch * 0.12, cw, ch * 0.88 + 2);
  ctx.fillStyle = rgba(lerpColor(t.far, t.A2, lit ? 0.3 : 0.14));
  roundRect(ctx, left - cw * 0.05, top, cw * 1.1, ch * 0.13, cw * 0.07);
  ctx.fill();
  ctx.stroke();
  if (!detail) return;
  const sx = left + cw * 0.14;
  const sy = top + ch * 0.19;
  const sw = cw * 0.72;
  const sh = ch * 0.3;
  ctx.fillStyle = rgba(lerpColor(t.far, t.ink, 0.5));
  roundRect(ctx, sx - cw * 0.05, sy - cw * 0.05, sw + cw * 0.1, sh + cw * 0.1, cw * 0.07);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(t.far, t.A1, lit ? 0.2 : 0.1));
  roundRect(ctx, sx, sy, sw, sh, cw * 0.09);
  ctx.fill();
  screenStatic(p, t, sx, sy, sw, sh);
  const py = top + ch * 0.55;
  ctx.fillStyle = rgba(lerpColor(t.far, t.rim, 0.08));
  ctx.beginPath();
  ctx.moveTo(left, py);
  ctx.lineTo(left + cw, py);
  ctx.lineTo(left + cw * 1.07, py + ch * 0.07);
  ctx.lineTo(left - cw * 0.07, py + ch * 0.07);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.55);
  ctx.stroke();
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.25));
  ctx.lineWidth = Math.max(1.2, cw * 0.03);
  ctx.beginPath();
  ctx.moveTo(left + cw * 0.26, py + ch * 0.025);
  ctx.lineTo(left + cw * 0.23, py - ch * 0.045);
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(t.far, t.L, 0.4));
  ctx.beginPath();
  ctx.arc(left + cw * 0.23, py - ch * 0.05, cw * 0.055, 0, TAU);
  ctx.fill();
  for (const [k, tone] of [
    [0.6, t.A1],
    [0.76, t.A2],
  ] as const) {
    ctx.fillStyle = rgba(lerpColor(t.far, tone, 0.35));
    ctx.beginPath();
    ctx.arc(left + cw * k, py + ch * 0.025, cw * 0.045, 0, TAU);
    ctx.fill();
  }
  const door = top + ch * 0.72;
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.14), 0.8);
  ctx.lineWidth = 1.2;
  ctx.strokeRect(x - cw * 0.16, door, cw * 0.32, ch * 0.15);
  ctx.fillStyle = rgba(lerpColor(t.far, t.A2, 0.4), 0.8);
  ctx.fillRect(x - cw * 0.1, door + ch * 0.04, cw * 0.05, ch * 0.035);
  ctx.fillRect(x + cw * 0.05, door + ch * 0.04, cw * 0.05, ch * 0.035);
  if (!lit) return;
  glowDot(ctx, x, sy + sh / 2, cw * 0.9, t.A1, 0.07);
  ctx.fillStyle = rgba(lerpColor(t.far, t.A2, 0.6), 0.55);
  starPath(ctx, x, top + ch * 0.066, ch * 0.042, 0.45, 5, 0);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(t.far, t.L, 0.55), 0.5);
  for (const d of [-0.34, -0.22, 0.22, 0.34]) {
    ctx.beginPath();
    ctx.arc(x + d * cw, top + ch * 0.066, Math.max(1.2, cw * 0.026), 0, TAU);
    ctx.fill();
  }
  if (!tilt) return;
  ctx.font = `400 ${String(Math.round(sh * 0.42))}px "${p.font}", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = rgba(lerpColor(t.far, t.A2, 0.55), 0.6);
  ctx.fillText('TILT', x, sy + sh * 0.53);
}

function cabinets(p: Painter, t: Tones, x0: number, x1: number, base: number, ch: number, level: number): void {
  const cw = ch * 0.5;
  const n = Math.max(2, Math.floor((x1 - x0) / (cw * 1.3)));
  const slot = (x1 - x0) / n;
  for (let i = 0; i < n; i++) {
    const x = x0 + slot * (i + 0.5);
    const k = HEIGHTS[i % HEIGHTS.length] ?? 1;
    const lit = i === 0;
    const detail = lit || !calmAt(p, x, base - ch * 0.66 * k, 0.95);
    cabinet(p, t, x, base, cw * k, ch * k, detail, lit, lit && level >= 4);
  }
}

function tokens(p: Painter, t: Tones, x0: number, x1: number, y0: number, y1: number): void {
  const ctx = p.ctx;
  const r = Math.max(5, p.unit * 0.024);
  let drawn = 0;
  for (let i = 0; i < 40 && drawn < 11; i++) {
    const x = x0 + p.rng.float() * Math.max(1, x1 - x0);
    const y = y0 + p.rng.float() * Math.max(1, y1 - y0);
    const stack = p.rng.chance(0.3) ? p.rng.int(2, 4) : 1;
    if (calmAt(p, x, y, 1.0)) continue;
    drawn += 1;
    for (let k = 0; k < stack; k++) {
      const cy = y - k * r * 0.24;
      ctx.fillStyle = rgba(lerpColor(t.far, t.A2, 0.3), 0.85);
      ctx.beginPath();
      ctx.ellipse(x, cy, r, r * 0.5, 0, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = rgba(t.ink, 0.6);
      ctx.lineWidth = 1.2;
      ctx.stroke();
    }
    const top = y - (stack - 1) * r * 0.24;
    ctx.strokeStyle = rgba(lerpColor(t.far, t.A2, 0.5), 0.6);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(x, top, r * 0.62, r * 0.31, 0, 0, TAU);
    ctx.stroke();
    ctx.save();
    ctx.translate(x, top);
    ctx.scale(1, 0.5);
    starPath(ctx, 0, 0, r * 0.32, 0.45, 5, 0);
    ctx.restore();
    ctx.fillStyle = rgba(lerpColor(t.far, t.A2, 0.55), 0.6);
    ctx.fill();
  }
}

function panel(p: Painter, t: Tones, edge: number, top: number, radius: number, side: -1 | 1): void {
  const ctx = p.ctx;
  const out = side > 0 ? p.w + 30 : -30;
  const gap = Math.max(8, p.unit * 0.03);
  const outline = (inset: number): void => {
    const e = edge + side * inset;
    const y = top + inset;
    const r = Math.max(4, radius - inset * 0.5);
    ctx.beginPath();
    ctx.moveTo(e, p.h + 30);
    ctx.lineTo(e, y + r);
    ctx.quadraticCurveTo(e, y, e + side * r, y);
    ctx.lineTo(out, y);
  };
  outline(0);
  ctx.lineTo(out, p.h + 30);
  ctx.closePath();
  ctx.fillStyle = rgba(lerpColor(t.bg, t.far, 0.9));
  ctx.fill();
  outline(0);
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.22), 0.75);
  ctx.lineWidth = Math.max(2, p.unit * 0.008);
  ctx.stroke();
  outline(gap);
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.18), 0.4);
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

function bumper(p: Painter, t: Tones, x: number, y: number, r: number): void {
  const ctx = p.ctx;
  glowDot(ctx, x, y, r * 2.2, t.A2, 0.09);
  ctx.fillStyle = rgba(t.ink, 0.45);
  ctx.beginPath();
  ctx.ellipse(x + r * 0.12, y + r * 0.18, r * 1.02, r * 0.98, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(t.ceil);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.L, 0.3), 0.55);
  ctx.beginPath();
  ctx.arc(x, y, r * 0.66, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.A2, 0.5), 0.55);
  starPath(ctx, x, y, r * 0.36, 0.45, 5, 0);
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.rim, 0.25), 0.6);
  ctx.lineWidth = Math.max(1.5, r * 0.07);
  sketchCircle(ctx, x, y, r * 0.84, p.rng, 0.02);
  ctx.strokeStyle = rgba(t.ink, 0.55);
  ctx.lineWidth = 1.2;
  sketchCircle(ctx, x, y, r * 0.66, p.rng, 0.02);
  ctx.strokeStyle = rgba(t.ink, 0.8);
  ctx.lineWidth = Math.max(1.5, r * 0.06);
  sketchCircle(ctx, x, y, r, p.rng, 0.03);
}

function trio(p: Painter, t: Tones, at: Trio): void {
  const { x, y, r } = at;
  bumper(p, t, x - 1.25 * r, y - 0.72 * r, r);
  bumper(p, t, x + 1.25 * r, y - 0.72 * r, r);
  bumper(p, t, x, y + 1.44 * r, r);
}

function lanes(p: Painter, t: Tones, at: Trio): void {
  const ctx = p.ctx;
  const { x, r } = at;
  const bottom = at.y - 1.72 * r - Math.max(4, r * 0.25);
  const top = bottom - r * 1.5;
  const step = r * 1.25;
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.24), 0.8);
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(2, r * 0.14);
  for (let k = 0; k < 4; k++) {
    const lx = x + (k - 1.5) * step;
    sketchLine(ctx, lx, top, lx, bottom, p.rng, 0.02);
  }
  for (let k = 0; k < 3; k++) {
    const lx = x + (k - 1) * step;
    const ly = (top + bottom) / 2;
    ctx.fillStyle = rgba(lerpColor(t.ceil, t.A2, 0.5), 0.55);
    ctx.beginPath();
    ctx.arc(lx, ly, r * 0.18, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = rgba(t.ink, 0.6);
    ctx.lineWidth = 1.2;
    ctx.stroke();
  }
}

function slingshot(p: Painter, t: Tones, x: number, y: number, s: number, side: -1 | 1): void {
  const ctx = p.ctx;
  const ax = x;
  const ay = y - s;
  const bx = x;
  const by = y + s * 0.35;
  const cx = x + side * s * 0.6;
  const cy = by + s * 0.6 * Math.tan(FLIP_ANGLE);
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.lineTo(bx, by);
  ctx.lineTo(cx, cy);
  ctx.closePath();
  ctx.fillStyle = rgba(t.ceil, 0.9);
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.7);
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.A1, 0.4), 0.55);
  ctx.lineWidth = Math.max(2, s * 0.05);
  sketchLine(ctx, ax + side * s * 0.06, ay + s * 0.1, cx - side * s * 0.04, cy - s * 0.08, p.rng, 0.01);
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.rim, 0.3), 0.8);
  for (const [px, py] of [
    [ax, ay],
    [bx, by],
    [cx, cy],
  ] as const) {
    ctx.beginPath();
    ctx.arc(px, py, Math.max(2, s * 0.05), 0, TAU);
    ctx.fill();
  }
}

function flipper(p: Painter, t: Tones, px: number, py: number, len: number, side: -1 | 1): Point {
  const ctx = p.ctx;
  const a = side > 0 ? FLIP_ANGLE : Math.PI - FLIP_ANGLE;
  const tx = px + Math.cos(a) * len;
  const ty = py + Math.sin(a) * len;
  const r0 = len * 0.16;
  const r1 = len * 0.07;
  const shape = (dx: number, dy: number): void => {
    ctx.beginPath();
    ctx.arc(px + dx, py + dy, r0, a + Math.PI / 2, a + (3 * Math.PI) / 2);
    ctx.arc(tx + dx, ty + dy, r1, a - Math.PI / 2, a + Math.PI / 2);
    ctx.closePath();
  };
  shape(r0 * 0.25, r0 * 0.35);
  ctx.fillStyle = rgba(t.ink, 0.4);
  ctx.fill();
  shape(0, 0);
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.rim, 0.12));
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.L, 0.4), 0.6);
  ctx.lineWidth = Math.max(2, r1 * 0.8);
  ctx.stroke();
  ctx.strokeStyle = rgba(t.ink, 0.6);
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.fillStyle = rgba(t.ink, 0.6);
  ctx.beginPath();
  ctx.arc(px, py, r0 * 0.35, 0, TAU);
  ctx.fill();
  return { x: tx, y: ty };
}

function ball(p: Painter, t: Tones, pivot: Point, tip: Point, len: number): void {
  const ctx = p.ctx;
  const r = len * 0.13;
  const k = 0.62;
  const ax = pivot.x + (tip.x - pivot.x) * k;
  const ay = pivot.y + (tip.y - pivot.y) * k;
  const ux = (tip.x - pivot.x) / len;
  const uy = (tip.y - pivot.y) / len;
  const nx = uy > 0 ? uy : -uy;
  const ny = uy > 0 ? -ux : ux;
  const up = ny < 0 ? 1 : -1;
  const lift = len * (0.16 + (0.07 - 0.16) * k) + r;
  const x = ax + nx * up * lift;
  const y = ay + ny * up * lift;
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.rim, 0.35), 0.9);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.3);
  ctx.lineWidth = 1;
  hatchCircle(ctx, x + r * 0.15, y + r * 0.15, r * 0.85, Math.max(2, r * 0.26), Math.PI / 4);
  ctx.fillStyle = rgba(t.rim, 0.5);
  ctx.beginPath();
  ctx.arc(x - r * 0.35, y - r * 0.38, r * 0.2, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.8);
  ctx.lineWidth = 1.4;
  sketchCircle(ctx, x, y, r, p.rng, 0.03);
}

function fieldBottom(p: Painter, x: number): number {
  const dx = (x - p.cx) / (1.08 * p.unit);
  return p.cy - 0.08 * p.unit + p.unit * Math.sqrt(Math.max(0, 1 - dx * dx));
}

export function paint(p: Painter, look: Look): void {
  const t = tonesOf(look);
  const aspect = p.w / p.h;
  const portrait = aspect < 0.8;
  const side = Math.max(0, p.cx - 1.08 * p.unit);
  const wide = !portrait && side >= p.unit * 0.42;
  const rowTop = portrait ? p.h * 0.2 + 4 : p.h * 0.2;
  const rowH = portrait ? p.h * 0.16 : p.h * 0.26;
  const floor = rowTop + rowH;
  room(p, t, floor);
  carpet(p, t, floor);
  cabinets(p, t, 0, wide ? p.w - side : p.w, floor, rowH, p.level);
  let at: Trio;
  let flipY: number;
  let flipLen: number;
  let flipIn: number;
  if (portrait) {
    const x = p.w * 0.86;
    const low = fieldBottom(p, x);
    const end = p.h * 0.84;
    const r = Math.max(10, Math.min((end - low) * 0.13, p.unit * 0.1));
    at = { x, y: (low + end) / 2 - 0.36 * r, r };
    flipY = p.h * 0.84;
    flipLen = Math.min(p.w * 0.28, p.unit * 0.42);
    flipIn = p.w * 0.07;
    tokens(p, t, p.w * 0.3, p.w * 0.7, p.h * 0.84, p.h * 0.92);
    panel(p, t, at.x - 3.4 * r, at.y - 1.72 * r - r * 2.4, r * 2.2, 1);
  } else if (wide) {
    const r = Math.min(p.unit * 0.1, side * 0.19);
    at = { x: p.w - side / 2, y: p.h * 0.43, r };
    flipY = p.h * 0.8;
    flipLen = Math.min(side * 0.75, p.unit * 0.34);
    flipIn = side * 0.22;
    tokens(p, t, side, p.w - side, p.h * 0.88, p.h * 0.985);
    panel(p, t, side - 8, p.h * 0.56, Math.min(side * 0.55, p.unit * 0.3), -1);
    panel(p, t, p.w - side + 8, p.h * 0.24, Math.min(side * 0.6, p.unit * 0.35), 1);
    const s = Math.min(side * 0.5, p.unit * 0.22);
    slingshot(p, t, side * 0.3, p.h * 0.7, s, 1);
    slingshot(p, t, p.w - side * 0.3, p.h * 0.7, s, -1);
  } else {
    const r = p.unit * 0.075;
    at = { x: p.w - 2.4 * r - 10, y: p.h * 0.74, r };
    flipY = p.h * 0.88;
    flipLen = p.unit * 0.3;
    flipIn = p.w * 0.05;
    tokens(p, t, p.w * 0.25, p.w * 0.75, p.h * 0.9, p.h * 0.985);
  }
  trio(p, t, at);
  const left = { x: flipIn, y: flipY };
  const right = { x: p.w - flipIn, y: flipY };
  const leftTip = flipper(p, t, left.x, left.y, flipLen, 1);
  const rightTip = flipper(p, t, right.x, right.y, flipLen, -1);
  if (p.level >= 2) lanes(p, t, at);
  if (p.level >= 3) {
    ball(p, t, left, leftTip, flipLen);
    ball(p, t, right, rightTip, flipLen);
  }
  calmCenter(p, t.bg, 0.62, 1.15);
  grain(p, 0.05, true);
  vignette(p, t.ink, 0.45);
}
